import { storableError } from '../util/errors';
import { fetchSkills, fetchTools, fetchSkillCategories } from '../util/api';
import { getSkillsAndToolsSource } from '../util/ssrDataSources';
import config from '../config';

// ================ Action types ================ //
export const FETCH_SKILLS_REQUEST = 'app/SkillsAndTools/FETCH_SKILLS_REQUEST';
export const FETCH_SKILLS_SUCCESS = 'app/SkillsAndTools/FETCH_SKILLS_SUCCESS';
export const FETCH_SKILLS_ERROR = 'app/SkillsAndTools/FETCH_SKILLS_ERROR';

export const FETCH_TOOLS_REQUEST = 'app/SkillsAndTools/FETCH_TOOLS_REQUEST';
export const FETCH_TOOLS_SUCCESS = 'app/SkillsAndTools/FETCH_TOOLS_SUCCESS';
export const FETCH_TOOLS_ERROR = 'app/SkillsAndTools/FETCH_TOOLS_ERROR';

export const FETCH_SKILL_CATEGORIES_REQUEST = 'app/SkillsAndTools/FETCH_SKILL_CATEGORIES_REQUEST';
export const FETCH_SKILL_CATEGORIES_SUCCESS = 'app/SkillsAndTools/FETCH_SKILL_CATEGORIES_SUCCESS';
export const FETCH_SKILL_CATEGORIES_ERROR = 'app/SkillsAndTools/FETCH_SKILL_CATEGORIES_ERROR';

// ================ Reducer ================ //
const initialState = {
  skillOptions: [],
  skillsFetchInProgress: false,
  skillsFetchError: null,
  toolOptions: [],
  toolsFetchInProgress: false,
  toolsFetchError: null,
  skillCategoryOptions: [],
  skillCategoriesFetchInProgress: false,
  skillCategoriesFetchError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SKILLS_REQUEST:
      return { ...state, skillsFetchInProgress: true, skillsFetchError: null };
    case FETCH_SKILLS_SUCCESS:
      return { ...state, skillOptions: payload, skillsFetchInProgress: false };
    case FETCH_SKILLS_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, skillsFetchInProgress: false, skillsFetchError: payload };

    case FETCH_TOOLS_REQUEST:
      return { ...state, toolsFetchInProgress: true, toolsFetchError: null };
    case FETCH_TOOLS_SUCCESS:
      return { ...state, toolOptions: payload, toolsFetchInProgress: false };
    case FETCH_TOOLS_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, toolsFetchInProgress: false, toolsFetchError: payload };

    case FETCH_SKILL_CATEGORIES_REQUEST:
      return {
        ...state,
        skillCategoriesFetchInProgress: true,
        skillCategoriesFetchError: null,
      };
    case FETCH_SKILL_CATEGORIES_SUCCESS:
      return {
        ...state,
        skillCategoryOptions: payload,
        skillCategoriesFetchInProgress: false,
      };
    case FETCH_SKILL_CATEGORIES_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        skillCategoriesFetchInProgress: false,
        skillCategoriesFetchError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchSkillsRequest = () => ({ type: FETCH_SKILLS_REQUEST });
export const fetchSkillsSuccess = skills => ({
  type: FETCH_SKILLS_SUCCESS,
  payload: skills,
});
export const fetchSkillsError = e => ({ type: FETCH_SKILLS_ERROR, payload: e });

export const fetchToolsRequest = () => ({ type: FETCH_TOOLS_REQUEST });
export const fetchToolsSuccess = tools => ({
  type: FETCH_TOOLS_SUCCESS,
  payload: tools,
});
export const fetchToolsError = e => ({ type: FETCH_TOOLS_ERROR, payload: e });

export const fetchSkillCategoriesRequest = () => ({
  type: FETCH_SKILL_CATEGORIES_REQUEST,
});
export const fetchSkillCategoriesSuccess = categories => ({
  type: FETCH_SKILL_CATEGORIES_SUCCESS,
  payload: categories,
});
export const fetchSkillCategoriesError = e => ({
  type: FETCH_SKILL_CATEGORIES_ERROR,
  payload: e,
});

// ================ Thunks ================ //

// Adds the locale's label to copies of the items (on the server the input is
// the shared cache, so it must not be mutated) and dispatches the lists:
// skills and tools sorted by label, categories by their order.
const dispatchSkillsAndTools = (dispatch, { skills, tools, categories }) => {
  const locale = config.locale;
  const translation = locale === 'fi' ? 'fiTranslation' : 'enTranslation';
  const withLabels = items => items.map(item => ({ ...item, label: item[translation] }));
  const byLabel = (a, b) => a.label.localeCompare(b.label, locale, { sensitivity: 'base' });

  dispatch(fetchSkillsSuccess(withLabels(skills).sort(byLabel)));
  dispatch(fetchToolsSuccess(withLabels(tools).sort(byLabel)));
  dispatch(
    fetchSkillCategoriesSuccess(
      withLabels(categories).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    )
  );
};

export const fetchSkillsAndTools = () => (dispatch, getState, sdk) => {
  if (typeof window === 'undefined') {
    // Server rendering reads the server's cache (registered in
    // server/index.js). Without it, or on failure, the state stays empty and
    // the browser fetches the lists on load as before.
    const source = getSkillsAndToolsSource();
    if (!source) {
      return Promise.resolve();
    }
    return Promise.resolve()
      .then(() => source())
      .then(data => {
        dispatchSkillsAndTools(dispatch, data);
      })
      .catch(() => {});
  }

  dispatch(fetchSkillsRequest());
  dispatch(fetchToolsRequest());
  dispatch(fetchSkillCategoriesRequest());

  return Promise.all([fetchSkills(), fetchTools(), fetchSkillCategories()])
    .then(([skills, tools, categories]) => {
      dispatchSkillsAndTools(dispatch, {
        skills: skills.items,
        tools: tools.items,
        categories: categories.items,
      });
    })
    .catch(e => {
      dispatch(fetchSkillsError(storableError(e)));
      dispatch(fetchToolsError(storableError(e)));
      dispatch(fetchSkillCategoriesError(storableError(e)));
    });
};
